/* eslint-disable no-console */
import { useState, useEffect, useCallback, useRef } from "react";

import clientUnifiedCommunicationService from "../services/clientUnifiedCommunicationService";
import { api_without_cancellation } from "../../../../../api/api";

// Initial pagination state for each tab
const createInitialPaginationState = () => ({
  all: {
    results: [],
    page: 1,
    hasNext: false,
    hasPrev: false,
    nextCursor: null,
    prevCursor: null,
    count: 0,
    loading: false,
  },
  chat: {
    results: [],
    page: 1,
    hasNext: false,
    hasPrev: false,
    nextCursor: null,
    prevCursor: null,
    count: 0,
    loading: false,
  },
  text: {
    results: [],
    page: 1,
    hasNext: false,
    hasPrev: false,
    nextCursor: null,
    prevCursor: null,
    count: 0,
    loading: false,
  },
  email: {
    results: [],
    page: 1,
    hasNext: false,
    hasPrev: false,
    nextCursor: null,
    prevCursor: null,
    count: 0,
    loading: false,
  },
  letter: {
    results: [],
    page: 1,
    hasNext: false,
    hasPrev: false,
    nextCursor: null,
    prevCursor: null,
    count: 0,
    loading: false,
  },
  phone_call: {
    results: [],
    page: 1,
    hasNext: false,
    hasPrev: false,
    nextCursor: null,
    prevCursor: null,
    count: 0,
    loading: false,
  },
});

export const useClientCommunications = (clientId, caseId, token) => {
  const [all, setAll] = useState([]);
  const [chat, setChat] = useState([]);
  const [text, setText] = useState([]);
  const [email, setEmail] = useState([]);
  const [letter, setLetter] = useState([]);
  const [phone, setPhone] = useState([]);
  const [call, setCall] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Pagination state for each tab
  const [pagination, setPagination] = useState(createInitialPaginationState);

  // Track if initial load has been done for each tab
  const initialLoadDone = useRef({
    all: false,
    chat: false,
    text: false,
    email: false,
    letter: false,
    phone_call: false,
  });

  // Legacy load function for backwards compatibility
  const _load = useCallback(async () => {
    if (!clientId || !caseId || !token) return;

    setLoading(true);
    setError(null);

    const { data, error } = await clientUnifiedCommunicationService.fetch(clientId, caseId, token);

    if (error) {
      setError(error);
      setLoading(false);
      return;
    }

    const parsed = clientUnifiedCommunicationService.normalize(data);

    setAll(parsed.all);
    setChat(parsed.chat);
    setText(parsed.text);
    setEmail(parsed.email);
    setLetter(parsed.letter);
    setPhone(parsed.phone);
    setCall(parsed?.call);

    setLoading(false);
  }, [clientId, caseId, token]);

  // Map tab keys to state setters
  const stateSetters = {
    all: setAll,
    chat: setChat,
    text: setText,
    email: setEmail,
    letter: setLetter,
    phone_call: setPhone,
  };

  // Fetch data for a specific tab with pagination
  const fetchTabData = useCallback(
    async (tabKey, cursor = null, direction = null) => {
      if (!clientId || !caseId || !token) return;

      // Set loading for this tab
      setPagination((prev) => ({
        ...prev,
        [tabKey]: { ...prev[tabKey], loading: true },
      }));

      const { data, error: fetchError } = await clientUnifiedCommunicationService.fetchByType(
        clientId,
        caseId,
        token,
        tabKey,
        cursor,
        direction
      );
      console.log("comm data:", data);

      if (fetchError) {
        console.error(`Error fetching ${tabKey}:`, fetchError);
        setPagination((prev) => ({
          ...prev,
          [tabKey]: { ...prev[tabKey], loading: false },
        }));
        return;
      }

      const results = data.results || [];
      const hasNext = data.has_next || false;
      const hasPrev = data.has_previous || false;
      const nextCursor = data.next_cursor || null;
      const prevCursor = data.prev_cursor || null;
      const count = data.count || 0;
      const currentPage = data.current_page || 1;

      // Update the corresponding state array for this tab
      if (tabKey === "phone_call") {
        setPhone(results.filter((i) => i.type === "PhoneCall"));
        setCall(
          results.filter(
            (i) => i.type === "OutboundRecordedCall" || i.type === "InboundRecordedCall"
          )
        );
      } else {
        const setter = stateSetters[tabKey];
        if (setter) {
          setter(results);
        }
      }

      setPagination((prev) => ({
        ...prev,
        [tabKey]: {
          ...prev[tabKey],
          results,
          hasNext,
          hasPrev,
          nextCursor,
          prevCursor,
          count,
          page: currentPage,
          loading: false,
        },
      }));

      initialLoadDone.current[tabKey] = true;
    },
    [clientId, caseId, token]
  );

  // Load initial data for a tab (first page)
  const loadTabInitial = useCallback(
    async (tabKey) => {
      if (initialLoadDone.current[tabKey]) return;
      await fetchTabData(tabKey, null, null);
    },
    [fetchTabData]
  );

  // Go to next page for a tab
  const goToNextPage = useCallback(
    async (tabKey) => {
      const tabState = pagination[tabKey];
      if (!tabState.hasNext || tabState.loading) return;

      await fetchTabData(tabKey, tabState.nextCursor, null);
    },
    [pagination, fetchTabData]
  );

  // Go to previous page for a tab (uses direction=newer)
  const goToPrevPage = useCallback(
    async (tabKey) => {
      const tabState = pagination[tabKey];
      if (!tabState.hasPrev || tabState.loading) return;

      await fetchTabData(tabKey, tabState.prevCursor, "newer");
    },
    [pagination, fetchTabData]
  );

  // Helper to add a new communication item to the appropriate state arrays
  const addCommunication = useCallback((newComm, type) => {
    // Helper to check if item already exists (avoid duplicates)
    const itemExists = (items, newItem) => {
      return items.some((item) => {
        // For emails: compare by id
        if (newItem.comm_type === "Email" || newItem.id) {
          return item.id === newItem.id;
        }

        // For text messages: compare by obj.id
        if (
          (newItem.type === "TextMessage" || newItem.type === "IncomingTextMessage") &&
          newItem.obj?.id
        ) {
          return item.obj?.id === newItem.obj?.id;
        }

        // For chat messages: compare by obj.id
        if (newItem.type === "ChatMessage" && newItem.obj?.id) {
          return item.obj?.id === newItem.obj?.id;
        }

        // if (newItem.type === "OutboundRecordedCall" && newItem.obj?.id) {
        //   return item.obj?.id === newItem.obj?.id;
        // }

        // For recorded calls (inbound + outbound share a list, ids overlap across tables)
        if (
          (newItem.type === "OutboundRecordedCall" || newItem.type === "InboundRecordedCall") &&
          newItem.obj?.id
        ) {
          return item.obj?.id === newItem.obj?.id && item.type === newItem.type;
        }

        // For phone calls and letters: compare by date and type (they might not have unique IDs in the WebSocket message)
        if (
          (newItem.type === "PhoneCall" || newItem.type === "LetterTemplateLog") &&
          item.type === newItem.type &&
          item.date &&
          newItem.date
        ) {
          const timeDiff = Math.abs(
            new Date(item.date).getTime() - new Date(newItem.date).getTime()
          );
          return timeDiff < 1000; // Within 1 second
        }

        return false;
      });
    };

    // Helper to sort items by date (newest first)
    const sortByDate = (items) => {
      console.log(items);
      return [...items].sort((a, b) => {
        const dateA = new Date(a.date || a.date_received || a.obj?.created_at || a.obj?.timestamp);
        const dateB = new Date(b.date || b.date_received || b.obj?.created_at || b.obj?.timestamp);
        return dateB - dateA;
      });
    };

    // Update "all" array
    // setAll((prev) => {
    //   if (itemExists(prev, newComm)) {
    //     console.log("Duplicate detected in 'all', skipping:", newComm);
    //     return prev;
    //   }
    //   return sortByDate([newComm, ...prev]);
    // });
    console.log(newComm);
    setAll((prev) => {
      const exisitingIndex = prev.findIndex(
        (item) => item?.obj?.id === newComm?.obj?.id && item?.type === newComm?.type
      );
      console.log(exisitingIndex);
      if (exisitingIndex !== -1) {
        const updated = [...prev];
        updated[exisitingIndex] = {
          ...updated[exisitingIndex],
          ...newComm,
          obj: { ...updated[exisitingIndex].obj, ...newComm?.obj },
        };
        console.log(updated);
        return updated;
      }
      return sortByDate([newComm, ...prev]);
    });

    // Update specific type arrays
    switch (type) {
      case "chat":
        setChat((prev) => {
          if (itemExists(prev, newComm)) return prev;
          return sortByDate([newComm, ...prev]);
        });
        break;
      case "text":
        setText((prev) => {
          if (itemExists(prev, newComm)) return prev;
          return sortByDate([newComm, ...prev]);
        });
        break;
      case "email":
        setEmail((prev) => {
          if (itemExists(prev, newComm)) return prev;
          return sortByDate([newComm, ...prev]);
        });
        break;
      case "letter":
        setLetter((prev) => {
          if (itemExists(prev, newComm)) return prev;
          return sortByDate([newComm, ...prev]);
        });
        break;
      case "phone":
        setPhone((prev) => {
          if (itemExists(prev, newComm)) return prev;
          return sortByDate([newComm, ...prev]);
        });
        break;
      case "call":
        setCall((prev) => {
          const exisitingIndex = prev.findIndex((item) => item?.obj?.id === newComm?.obj?.id);
          if (exisitingIndex !== -1) {
            const updated = [...prev];
            updated[exisitingIndex] = {
              ...updated[exisitingIndex],
              ...newComm,
              obj: { ...updated[exisitingIndex].obj, ...newComm?.obj },
            };
            console.log(updated);
            return updated;
          }
          return sortByDate([newComm, ...prev]);
        });
        break;
      default:
        console.warn("Unknown communication type:", type);
    }
  }, []);
  const markItemAsReviewed = useCallback(
    async (item) => {
      if (!item) return;

      // Determine if already reviewed
      const reviewed = item.is_reviewed ?? item.obj?.is_reviewed;
      if (reviewed) return; // already reviewed

      // Build payload
      const type = item.comm_type || item.type;
      let communication_type;
      let object_id;

      switch (type) {
        case "IncomingTextMessage":
          communication_type = "text";
          object_id = item.obj?.id;
          break;
        case "TextMessage":
          communication_type = "outbound_text";
          object_id = item.obj?.id;
          break;
        case "PhoneCall":
          communication_type = "phone_call";
          object_id = item.id || item.obj?.id;
          break;
        case "InboundRecordedCall":
          communication_type = "phone_call";
          object_id = item.obj?.id;
          break;
        case "OutboundRecordedCall":
          communication_type = "phone_call";
          object_id = item.obj?.id;
          break;
        case "ChatMessage":
          communication_type = "chat";
          object_id = item.obj?.id;
          break;
        case "LetterTemplateLog":
          communication_type = "letter";
          object_id = item.id || item.obj?.id;
          break;

        default:
          console.warn("Unknown type for mark as reviewed:", type);
          return;
      }

      if (!object_id) {
        console.warn("Missing object_id for mark as reviewed:", item);
        return;
      }

      // Optimistically update local state
      const updateItem = (items) =>
        items.map((i) => {
          const id = i.id || i.obj?.id;
          if (id === object_id) {
            if (i.obj) {
              return { ...i, obj: { ...i.obj, is_reviewed: true } };
            }
            return { ...i, is_reviewed: true };
          }
          return i;
        });

      // Update all relevant arrays
      setAll(updateItem);
      // Also update specific tab arrays (if any)
      setChat(updateItem);
      setText(updateItem);
      setEmail(updateItem);
      setLetter(updateItem);
      setPhone(updateItem);
      setCall(updateItem);

      // Call the API
      try {
        await api_without_cancellation.post(
          "/api/client-page/communications/mark-reviewed/",
          { communication_type, object_id },
          { headers: { Authorization: token } }
        );
        console.log(`✅ Marked ${communication_type} (${object_id}) as reviewed`);
      } catch (error) {
        console.error("Failed to mark as reviewed:", error);
        // Optionally revert the optimistic update if the API fails
        // (for simplicity, we leave the optimistic change – the server will eventually reflect the correct state on refetch)
      }
    },
    [origin, token]
  );

  // Load "all" tab on initial mount
  useEffect(() => {
    if (clientId && caseId && token) {
      fetchTabData("all", null);
    }
  }, [clientId, caseId, token, fetchTabData]);

  return {
    all,
    chat,
    text,
    email,
    letter,
    phone,
    call,
    loading,
    error,
    refetch_data: () => fetchTabData("all", null),
    addCommunication, // Expose this for WebSocket integration
    // Pagination exports
    pagination,
    loadTabInitial,
    goToNextPage,
    goToPrevPage,
    markItemAsReviewed,
  };
};
